import React, { useState, useEffect, useCallback } from 'react';
import {
  tagService,
  TagItem,
  TagStats,
  CreateTagRequest,
} from '../../services/tagService';

type ViewMode = 'table' | 'stats';

// ─── Tag Form Modal ─────────────────────────────────────────────────
interface TagFormModalProps {
  isOpen: boolean;
  tag?: TagItem | null;
  currentDefaultTag: TagItem | null;
  onClose: () => void;
  onSave: () => void;
}

const TagFormModal: React.FC<TagFormModalProps> = ({
  isOpen,
  tag,
  currentDefaultTag,
  onClose,
  onSave,
}) => {
  const [tagName, setTagName] = useState('');
  const [tagValues, setTagValues] = useState<string[]>([]);
  const [newValue, setNewValue] = useState('');
  const [description, setDescription] = useState('');
  const [useAsRagFilter, setUseAsRagFilter] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [overrideConfirmOpen, setOverrideConfirmOpen] = useState(false);

  useEffect(() => {
    if (tag) {
      setTagName(tag.tag_name);
      setTagValues(tag.tag_values || []);
      setDescription(tag.description || '');
      setUseAsRagFilter(tag.is_default);
    } else {
      setTagName('');
      setTagValues([]);
      setDescription('');
      setUseAsRagFilter(false);
    }
    setNewValue('');
    setError('');
  }, [tag, isOpen]);

  const handleAddValue = () => {
    const v = newValue.trim();
    if (v && !tagValues.includes(v)) {
      setTagValues([...tagValues, v]);
      setNewValue('');
    }
  };

  const handleRemoveValue = (idx: number) => {
    setTagValues(tagValues.filter((_, i) => i !== idx));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const pendingValue = newValue.trim();
    const nextTagValues =
      pendingValue && !tagValues.includes(pendingValue)
        ? [...tagValues, pendingValue]
        : tagValues;

    if (!tagName.trim()) {
      setError('Tag field name is required');
      return;
    }
    if (nextTagValues.length === 0) {
      setError('At least one tag value is required');
      return;
    }

    if (nextTagValues !== tagValues) {
      setTagValues(nextTagValues);
      setNewValue('');
    }

    if (useAsRagFilter && currentDefaultTag && currentDefaultTag.id !== tag?.id) {
      setOverrideConfirmOpen(true);
      return;
    }

    await executeSave(nextTagValues);
  };

  const executeSave = async (valuesToSave: string[] = tagValues) => {
    setSaving(true);
    setError('');
    setOverrideConfirmOpen(false);
    try {
      const payload: CreateTagRequest = {
        tag_name: tagName.trim(),
        tag_values: valuesToSave,
        description: description.trim() || null,
      };
      let savedId: string;
      if (tag) {
        await tagService.updateTag(tag.id, {
          tag_name: payload.tag_name,
          tag_values: payload.tag_values,
          description: payload.description,
        });
        savedId = tag.id;
      } else {
        const created = await tagService.createTag(payload);
        savedId = created.id;
      }
      if (useAsRagFilter) {
        await tagService.setDefault(savedId);
      }
      onSave();
      onClose();
    } catch (err: any) {
      setError(
        err?.response?.data?.detail || err?.message || 'Failed to save tag',
      );
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md mx-4">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-800">
            {tag ? 'Edit Tag Field' : 'Add New Tag Field'}
          </h3>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Field Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={tagName}
              onChange={(e) => setTagName(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="e.g. RAG_tag, Fund"
              autoFocus
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Values <span className="text-red-500">*</span>
            </label>
            <div className="flex gap-2 mb-2">
              <input
                type="text"
                value={newValue}
                onChange={(e) => setNewValue(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleAddValue();
                  }
                }}
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Add value..."
              />
              <button
                type="button"
                onClick={handleAddValue}
                className="px-3 py-2 text-sm font-medium text-blue-600 border border-blue-600 rounded-lg hover:bg-blue-50 transition-colors"
              >
                Add
              </button>
            </div>
            <div className="flex flex-wrap gap-2">
              {tagValues.map((v, i) => (
                <span
                  key={i}
                  className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-gray-100 text-gray-800 rounded-full"
                >
                  {v}
                  <button
                    type="button"
                    onClick={() => handleRemoveValue(i)}
                    className="text-gray-500 hover:text-red-600"
                    aria-label="Remove"
                  >
                    ×
                  </button>
                </span>
              ))}
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Description
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              rows={3}
              placeholder="Optional description"
            />
          </div>

          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={useAsRagFilter}
              onChange={(e) => setUseAsRagFilter(e.target.checked)}
              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            <span className="text-sm text-gray-700">
              Use as RAG retrieval filter
            </span>
          </label>
          {useAsRagFilter && currentDefaultTag && currentDefaultTag.id !== tag?.id && (
            <p className="text-xs text-amber-600 bg-amber-50 px-2 py-1.5 rounded">
              &quot;{currentDefaultTag.tag_name}&quot; is currently used for RAG retrieval. Saving will override it.
            </p>
          )}

          {error && (
            <p className="text-sm text-red-600 bg-red-50 px-3 py-2 rounded-lg">
              {error}
            </p>
          )}

          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
              disabled={saving}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
            >
              {saving ? 'Saving...' : tag ? 'Update' : 'Create'}
            </button>
          </div>
        </form>
      </div>

      {/* Override RAG filter confirmation */}
      {overrideConfirmOpen && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-sm mx-4 p-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-2">
              Override RAG retrieval filter?
            </h3>
            <p className="text-sm text-gray-600 mb-4">
              &quot;{currentDefaultTag?.tag_name}&quot; is currently used for RAG retrieval. Do you want to use &quot;{tagName.trim()}&quot; instead?
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setOverrideConfirmOpen(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
              >
                Cancel
              </button>
              <button
                onClick={() => executeSave(tagValues)}
                disabled={saving}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50"
              >
                Yes, override
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ─── Confirm Dialog ─────────────────────────────────────────────────
interface ConfirmDialogProps {
  isOpen: boolean;
  title: string;
  message: string;
  warning?: string | null;
  error?: string | null;
  confirmLabel?: string;
  onConfirm: () => void;
  onCancel: () => void;
}

const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
  isOpen,
  title,
  message,
  warning,
  error,
  confirmLabel = 'Confirm',
  onConfirm,
  onCancel,
}) => {
  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-sm mx-4 p-6">
        <h3 className="text-lg font-semibold text-gray-800 mb-2">{title}</h3>
        <p className="text-sm text-gray-600 mb-3">{message}</p>
        {warning && (
          <div className="mb-4 px-3 py-2 bg-amber-50 border border-amber-200 rounded-lg">
            <p className="text-sm text-amber-800">{warning}</p>
          </div>
        )}
        {error && (
          <div className="mb-4 px-3 py-2 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-sm text-red-700">{error}</p>
          </div>
        )}
        <div className="flex justify-end gap-3">
          <button
            onClick={onCancel}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors"
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Stats View ─────────────────────────────────────────────────────
const StatsView: React.FC<{
  stats: TagStats | null;
  loading: boolean;
  fileVectorCounts: Record<string, number>;
  fileCountsLoading: boolean;
}> = ({
  stats,
  loading,
  fileVectorCounts,
  fileCountsLoading,
}) => {
  if (loading) {
    return (
      <div className="text-center py-12 text-gray-500">
        Loading stats from Pinecone...
      </div>
    );
  }
  if (!stats) {
    return (
      <div className="text-center py-12 text-gray-500">
        Unable to load stats.
      </div>
    );
  }

  const maxCount = Math.max(...stats.tags.map((t) => t.vector_count), 1);

  const fileCounts = Object.entries(fileVectorCounts).sort((a, b) => b[1] - a[1]);
  const maxFileCount = fileCounts.length > 0 ? Math.max(...fileCounts.map((e) => e[1]), 1) : 1;

  return (
    <div className="space-y-6">
      {/* Summary cards */}
      <div className="grid grid-cols-3 gap-4">
        <div className="bg-blue-50 rounded-xl p-4">
          <p className="text-xs font-medium text-blue-600 uppercase tracking-wider">
            Total Vectors
          </p>
          <p className="text-2xl font-bold text-blue-900 mt-1">
            {stats.total_vectors.toLocaleString()}
          </p>
        </div>
        <div className="bg-green-50 rounded-xl p-4">
          <p className="text-xs font-medium text-green-600 uppercase tracking-wider">
            Tag Fields
          </p>
          <p className="text-2xl font-bold text-green-900 mt-1">
            {stats.tag_count}
          </p>
        </div>
        <div className="bg-purple-50 rounded-xl p-4">
          <p className="text-xs font-medium text-purple-600 uppercase tracking-wider">
            Dimensions
          </p>
          <p className="text-2xl font-bold text-purple-900 mt-1">
            {stats.dimension}
          </p>
        </div>
      </div>

      {/* Distribution bars by tag */}
      <div className="bg-white border border-gray-200 rounded-xl p-6">
        <h3 className="text-sm font-semibold text-gray-800 mb-4">
          Vector Distribution by Tag Field
        </h3>
        <div className="space-y-3">
          {stats.tags.map((t) => {
            const pct = maxCount > 0 ? (t.vector_count / maxCount) * 100 : 0;
            return (
              <div key={t.id}>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm text-gray-700 font-medium">
                    {t.tag_name}
                    {t.is_default && (
                      <span className="ml-2 text-xs bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded">
                        RAG filter
                      </span>
                    )}
                  </span>
                  <span className="text-sm text-gray-500 tabular-nums">
                    {t.vector_count.toLocaleString()}
                  </span>
                </div>
                <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-blue-500 rounded-full transition-all"
                    style={{ width: `${pct}%` }}
                  />
                </div>
              </div>
            );
          })}
          {stats.tags.length === 0 && (
            <p className="text-sm text-gray-400 text-center py-4">
              No active tag fields
            </p>
          )}
        </div>
      </div>

      {/* Per-file vector counts */}
      <div className="bg-white border border-gray-200 rounded-xl p-6">
        <h3 className="text-sm font-semibold text-gray-800 mb-4">
          Vectors per File
        </h3>
        {fileCountsLoading ? (
          <div className="text-center py-8 text-gray-500 text-sm">Loading file vector counts...</div>
        ) : fileCounts.length === 0 ? (
          <p className="text-sm text-gray-400 text-center py-4">No ingested files</p>
        ) : (
          <div className="space-y-2 max-h-80 overflow-y-auto">
            {fileCounts.map(([filename, count]) => {
              const pct = maxFileCount > 0 ? (count / maxFileCount) * 100 : 0;
              return (
                <div key={filename}>
                  <div className="flex items-center justify-between mb-0.5">
                    <span className="text-xs text-gray-700 truncate max-w-[320px]" title={filename}>
                      {filename}
                    </span>
                    <span className="text-xs text-gray-500 tabular-nums ml-2 flex-shrink-0">
                      {count.toLocaleString()}
                    </span>
                  </div>
                  <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-indigo-400 rounded-full transition-all"
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

// ─── Main Page ──────────────────────────────────────────────────────
const TagsConfigPage: React.FC = () => {
  const [tags, setTags] = useState<TagItem[]>([]);
  const [stats, setStats] = useState<TagStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [statsLoading, setStatsLoading] = useState(false);
  const [viewMode, setViewMode] = useState<ViewMode>('table');
  const [fileVectorCounts, setFileVectorCounts] = useState<Record<string, number>>({});
  const [fileCountsLoading, setFileCountsLoading] = useState(false);

  // Modals
  const [formOpen, setFormOpen] = useState(false);
  const [editingTag, setEditingTag] = useState<TagItem | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmTag, setConfirmTag] = useState<TagItem | null>(null);
  const [confirmWarning, setConfirmWarning] = useState<string | null>(null);
  const [confirmAction, setConfirmAction] = useState<'deactivate' | 'delete'>('deactivate');
  const [confirmError, setConfirmError] = useState<string | null>(null);
  const [ragOverrideOpen, setRagOverrideOpen] = useState(false);
  const [ragOverrideTag, setRagOverrideTag] = useState<TagItem | null>(null);
  const [ragFilterError, setRagFilterError] = useState<string | null>(null);

  const fetchTags = useCallback(async () => {
    setLoading(true);
    try {
      const data = await tagService.listTags();
      setTags(data);
    } catch {
      /* swallow */
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchStats = useCallback(async () => {
    setStatsLoading(true);
    setFileCountsLoading(true);
    try {
      const [statsData, fileCounts] = await Promise.all([
        tagService.getStats(),
        tagService.getFileVectorCounts().catch(() => ({})),
      ]);
      setStats(statsData);
      setFileVectorCounts(fileCounts);
    } catch {
      /* swallow */
    } finally {
      setStatsLoading(false);
      setFileCountsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTags();
  }, [fetchTags]);

  useEffect(() => {
    if (viewMode === 'stats') fetchStats();
  }, [viewMode, fetchStats]);

  // Handlers
  const openCreate = () => {
    setEditingTag(null);
    setFormOpen(true);
  };

  const openEdit = (tag: TagItem) => {
    setEditingTag(tag);
    setFormOpen(true);
  };

  const handleToggleActive = async (tag: TagItem) => {
    if (tag.is_active) {
      try {
        const detail = await tagService.getTag(tag.id);
        setConfirmTag(tag);
        setConfirmAction('deactivate');
        setConfirmError(null);
        setConfirmWarning(
          detail.vector_count && detail.vector_count > 0
            ? `This tag has approximately ${detail.vector_count.toLocaleString()} vectors in Pinecone. Deactivating it will exclude these vectors from search results.`
            : null,
        );
        setConfirmOpen(true);
      } catch {
        setConfirmTag(tag);
        setConfirmAction('deactivate');
        setConfirmError(null);
        setConfirmWarning(null);
        setConfirmOpen(true);
      }
    } else {
      await tagService.updateTag(tag.id, { is_active: true });
      fetchTags();
    }
  };

  const handleDeleteTag = async (tag: TagItem) => {
    try {
      const detail = await tagService.getTag(tag.id);
      setConfirmTag(tag);
      setConfirmAction('delete');
      setConfirmError(null);
      setConfirmWarning(
        detail.vector_count && detail.vector_count > 0
          ? `This tag has approximately ${detail.vector_count.toLocaleString()} vectors in Pinecone. Deleting the tag will remove it from this configuration, and those vectors remain excluded while this field is not used for retrieval.`
          : null,
      );
      setConfirmOpen(true);
    } catch {
      setConfirmTag(tag);
      setConfirmAction('delete');
      setConfirmError(null);
      setConfirmWarning(null);
      setConfirmOpen(true);
    }
  };

  const confirmDeactivate = async () => {
    if (!confirmTag) return;
    try {
      if (confirmAction === 'delete') {
        await tagService.deleteTagPermanent(confirmTag.id);
      } else {
        await tagService.deleteTag(confirmTag.id);
      }
    } catch (err: any) {
      const msg = err?.response?.data?.detail || err?.message || 'Action failed';
      setConfirmError(msg);
      return;
    }
    setConfirmOpen(false);
    setConfirmTag(null);
    setConfirmWarning(null);
    setConfirmAction('deactivate');
    setConfirmError(null);
    fetchTags();
  };

  const handleSetAsRagFilter = (tag: TagItem) => {
    setRagFilterError(null);
    const currentDefault = tags.find((t) => t.is_default);
    if (currentDefault && currentDefault.id !== tag.id) {
      setRagOverrideTag(tag);
      setRagOverrideOpen(true);
    } else {
      doSetAsRagFilter(tag.id);
    }
  };

  const doSetAsRagFilter = async (tagId: string) => {
    setRagFilterError(null);
    try {
      await tagService.setDefault(tagId);
      await fetchTags();
    } catch (err: any) {
      const msg = err?.response?.data?.detail || err?.message || 'Failed to set RAG filter';
      setRagFilterError(msg);
      return;
    }
    setRagOverrideOpen(false);
    setRagOverrideTag(null);
  };

  return (
    <div className="w-full py-2">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-xl font-semibold text-gray-800">
            Metadata Tags Configuration
          </h1>
          <p className="text-sm text-gray-500 mt-0.5 leading-relaxed">
            The tag marked as &quot;RAG Filter&quot; is used for retrieval.
            <br />
            Use the checkbox when adding a tag,
            <br />
            or click &quot;Use for RAG&quot; on an existing tag.
          </p>
          {ragFilterError && !ragOverrideOpen && (
            <p className="text-sm text-red-600 bg-red-50 px-3 py-1.5 rounded mt-2">
              {ragFilterError}
            </p>
          )}
        </div>
        <div className="flex items-center gap-3">
          {/* View toggle */}
          <div className="flex bg-gray-100 rounded-lg p-0.5">
            <button
              onClick={() => setViewMode('table')}
              className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                viewMode === 'table'
                  ? 'bg-white text-gray-800 shadow-sm'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              Tag Table
            </button>
            <button
              onClick={() => setViewMode('stats')}
              className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                viewMode === 'stats'
                  ? 'bg-white text-gray-800 shadow-sm'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              Stats
            </button>
          </div>

          {viewMode === 'table' && (
            <button
              onClick={openCreate}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors"
            >
              + Add Tag Field
            </button>
          )}
        </div>
      </div>

      {viewMode === 'stats' ? (
        <StatsView stats={stats} loading={statsLoading} fileVectorCounts={fileVectorCounts} fileCountsLoading={fileCountsLoading} />
      ) : (
        <>
          {/* Table */}
          <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
            {loading ? (
              <div className="text-center py-12 text-gray-500">Loading...</div>
            ) : tags.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                No tag fields found. Click "+ Add Tag Field" to create one.
              </div>
            ) : (
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-4 py-3 text-left font-medium text-gray-600">
                      Field Name
                    </th>
                    <th className="px-4 py-3 text-left font-medium text-gray-600">
                      Values
                    </th>
                    <th className="px-4 py-3 text-left font-medium text-gray-600">
                      Description
                    </th>
                    <th className="px-4 py-3 text-center font-medium text-gray-600">
                      Status
                    </th>
                    <th className="px-4 py-3 text-center font-medium text-gray-600" title="RAG retrieval filter field">
                      RAG Filter
                    </th>
                    <th className="px-4 py-3 text-left font-medium text-gray-600">
                      Created
                    </th>
                    <th className="px-4 py-3 text-right font-medium text-gray-600">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {tags.map((tag) => (
                    <tr
                      key={tag.id}
                      className={`hover:bg-gray-50 transition-colors ${
                        !tag.is_active ? 'opacity-50' : ''
                      }`}
                    >
                      <td className="px-4 py-3 font-medium text-gray-800">
                        {tag.tag_name}
                      </td>
                      <td className="px-4 py-3 text-gray-600 max-w-[200px]">
                        <div className="flex flex-wrap gap-1">
                          {(tag.tag_values || []).map((v, i) => (
                            <span
                              key={i}
                              className="inline-block px-2 py-0.5 text-xs bg-gray-100 text-gray-700 rounded"
                            >
                              {v}
                            </span>
                          ))}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-gray-500 max-w-[200px] truncate">
                        {tag.description || '—'}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span
                          className={`inline-block w-2.5 h-2.5 rounded-full ${
                            tag.is_active ? 'bg-green-500' : 'bg-gray-300'
                          }`}
                          title={tag.is_active ? 'Active' : 'Inactive'}
                        />
                      </td>
                      <td className="px-4 py-3 text-center">
                        {tag.is_default ? (
                          <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded font-medium" title="Used for RAG retrieval">
                            Yes
                          </span>
                        ) : tag.is_active ? (
                          <button
                            onClick={() => handleSetAsRagFilter(tag)}
                            className="px-2 py-1 text-xs font-medium text-blue-600 border border-blue-200 rounded hover:bg-blue-50 transition-colors"
                            title="Use this tag field for RAG retrieval filtering"
                          >
                            Use for RAG
                          </button>
                        ) : null}
                      </td>
                      <td className="px-4 py-3 text-gray-500 whitespace-nowrap">
                        {tag.created_at
                          ? new Date(tag.created_at).toLocaleDateString()
                          : '—'}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={() => openEdit(tag)}
                            className="text-xs text-blue-600 hover:text-blue-800 font-medium transition-colors"
                          >
                            Edit
                          </button>
                          {tag.is_active ? (
                            <button
                              onClick={() => handleToggleActive(tag)}
                              className="text-xs font-medium text-amber-600 hover:text-amber-800 transition-colors"
                            >
                              Deactivate
                            </button>
                          ) : (
                            <>
                              <button
                                onClick={() => handleToggleActive(tag)}
                                className="text-xs font-medium text-green-600 hover:text-green-800 transition-colors"
                              >
                                Activate
                              </button>
                              <button
                                onClick={() => handleDeleteTag(tag)}
                                className="text-xs font-medium text-red-600 hover:text-red-800 transition-colors"
                              >
                                Delete
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </>
      )}

      {/* Modals */}
      <TagFormModal
        isOpen={formOpen}
        tag={editingTag}
        currentDefaultTag={tags.find((t) => t.is_default) ?? null}
        onClose={() => setFormOpen(false)}
        onSave={fetchTags}
      />

      <ConfirmDialog
        isOpen={confirmOpen}
        title={confirmAction === 'delete' ? 'Delete Tag Field' : 'Deactivate Tag Field'}
        message={
          confirmAction === 'delete'
            ? `Are you sure you want to delete "${confirmTag?.tag_name}"? It will be removed from Metadata Tags Configuration.`
            : `Are you sure you want to deactivate "${confirmTag?.tag_name}"?`
        }
        warning={confirmWarning}
        error={confirmError}
        confirmLabel={confirmAction === 'delete' ? 'Delete' : 'Deactivate'}
        onConfirm={confirmDeactivate}
        onCancel={() => {
          setConfirmOpen(false);
          setConfirmTag(null);
          setConfirmWarning(null);
          setConfirmAction('deactivate');
          setConfirmError(null);
        }}
      />

      {/* Override RAG filter confirm (from table) */}
      {ragOverrideOpen && ragOverrideTag && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-sm mx-4 p-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-2">
              Override RAG retrieval filter?
            </h3>
            <p className="text-sm text-gray-600 mb-4">
              Another tag is currently used for RAG retrieval. Do you want to use &quot;{ragOverrideTag.tag_name}&quot; instead?
            </p>
            {ragFilterError && (
              <p className="text-sm text-red-600 bg-red-50 px-2 py-1.5 rounded mb-4">
                {ragFilterError}
              </p>
            )}
            <div className="flex justify-end gap-3">
              <button
                onClick={() => {
                  setRagOverrideOpen(false);
                  setRagOverrideTag(null);
                  setRagFilterError(null);
                }}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
              >
                Cancel
              </button>
              <button
                onClick={() => doSetAsRagFilter(ragOverrideTag.id)}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700"
              >
                Yes, use for RAG
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TagsConfigPage;
